export enum TicketStatus {
  Open = 0,
  InProgress = 1,
  Escalated = 2,
  Resolved = 3,
  Closed = 4
}

export enum TicketPriority {
  Critical = 0,
  High = 1,
  Medium = 2,
  Low = 3
}

export enum TicketCategory {
  Hardware = 0,
  Software = 1,
  // Network = 2 removed — value reserved for legacy data
  UserAccess = 3,
  Firmware = 4,
  Configuration = 5,
  Other = 6
}

export enum TicketSubCategory {
  General = 0,       // legacy only
  OtherLegacy = 99,  // legacy catch-all

  // Hardware (1xx)
  Battery = 100, Inverter = 101, Cable = 102, Gateway = 103,
  Metering = 104, PV = 105,
  HardwareOther = 199,

  // Software (2xx)
  Monitor = 200, ControllerService = 201, ModbusTcpService = 202, EMS = 203,
  SoftwareOther = 299,

  // Network (3xx) — legacy
  Connectivity = 300, VpnAccess = 301, S3Storage = 302,

  // UserAccess (4xx)
  Login = 400, Permissions = 401,
  UserAccessOther = 499,

  // Firmware (5xx)
  BatteryFirmware = 500, InverterFirmware = 501, ControllerFirmware = 502,
  ExternalEmsFirmware = 503,
  FirmwareOther = 599,

  // Configuration (6xx)
  BMS = 600, ConfigMonitor = 601, ExternalEMS = 602,
  ConfigurationOther = 699,

  // Other (7xx) — generic bucket; default for partner-created tickets before triage
  Other = 700
}

// Display labels for all subcategories (including legacy ones for backward compat)
export const subCategoryLabels: Record<number, string> = {
  [TicketSubCategory.General]: 'General',
  [TicketSubCategory.OtherLegacy]: 'Other',
  [TicketSubCategory.Battery]: 'Battery',
  [TicketSubCategory.Inverter]: 'Inverter',
  [TicketSubCategory.Cable]: 'Cable',
  [TicketSubCategory.Gateway]: 'Gateway',
  [TicketSubCategory.Metering]: 'Metering',
  [TicketSubCategory.PV]: 'PV',
  [TicketSubCategory.HardwareOther]: 'Other',
  [TicketSubCategory.Monitor]: 'Monitor',
  [TicketSubCategory.ControllerService]: 'Controller Service',
  [TicketSubCategory.ModbusTcpService]: 'Modbus TCP Service',
  [TicketSubCategory.EMS]: 'EMS',
  [TicketSubCategory.SoftwareOther]: 'Other',
  [TicketSubCategory.Connectivity]: 'Connectivity',
  [TicketSubCategory.VpnAccess]: 'VPN Access',
  [TicketSubCategory.S3Storage]: 'S3 Storage',
  [TicketSubCategory.Login]: 'Login',
  [TicketSubCategory.Permissions]: 'Permissions',
  [TicketSubCategory.UserAccessOther]: 'Other',
  [TicketSubCategory.BatteryFirmware]: 'Battery Firmware',
  [TicketSubCategory.InverterFirmware]: 'Inverter Firmware',
  [TicketSubCategory.ControllerFirmware]: 'Controller Firmware',
  [TicketSubCategory.ExternalEmsFirmware]: 'External EMS Firmware',
  [TicketSubCategory.FirmwareOther]: 'Other',
  [TicketSubCategory.BMS]: 'BMS',
  [TicketSubCategory.ConfigMonitor]: 'Monitor',
  [TicketSubCategory.ExternalEMS]: 'External EMS',
  [TicketSubCategory.ConfigurationOther]: 'Other',
  [TicketSubCategory.Other]: 'Other'
};

export const subCategoryKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TicketSubCategory.General]:              { id: 'subCatGeneral',              defaultMessage: 'General' },
  [TicketSubCategory.OtherLegacy]:          { id: 'subCatOtherLegacy',          defaultMessage: 'Other' },
  [TicketSubCategory.Battery]:              { id: 'subCatBattery',              defaultMessage: 'Battery' },
  [TicketSubCategory.Inverter]:             { id: 'subCatInverter',             defaultMessage: 'Inverter' },
  [TicketSubCategory.Cable]:                { id: 'subCatCable',                defaultMessage: 'Cable' },
  [TicketSubCategory.Gateway]:              { id: 'subCatGateway',              defaultMessage: 'Gateway' },
  [TicketSubCategory.Metering]:             { id: 'subCatMetering',             defaultMessage: 'Metering' },
  [TicketSubCategory.PV]:                   { id: 'subCatPV',                   defaultMessage: 'PV' },
  [TicketSubCategory.HardwareOther]:        { id: 'subCatHardwareOther',        defaultMessage: 'Other' },
  [TicketSubCategory.Monitor]:              { id: 'subCatMonitor',              defaultMessage: 'Monitor' },
  [TicketSubCategory.ControllerService]:    { id: 'subCatControllerService',    defaultMessage: 'Controller Service' },
  [TicketSubCategory.ModbusTcpService]:     { id: 'subCatModbusTcpService',     defaultMessage: 'Modbus TCP Service' },
  [TicketSubCategory.EMS]:                  { id: 'subCatEMS',                  defaultMessage: 'EMS' },
  [TicketSubCategory.SoftwareOther]:        { id: 'subCatSoftwareOther',        defaultMessage: 'Other' },
  [TicketSubCategory.Connectivity]:         { id: 'subCatConnectivity',         defaultMessage: 'Connectivity' },
  [TicketSubCategory.VpnAccess]:            { id: 'subCatVpnAccess',            defaultMessage: 'VPN Access' },
  [TicketSubCategory.S3Storage]:            { id: 'subCatS3Storage',            defaultMessage: 'S3 Storage' },
  [TicketSubCategory.Login]:                { id: 'subCatLogin',                defaultMessage: 'Login' },
  [TicketSubCategory.Permissions]:          { id: 'subCatPermissions',          defaultMessage: 'Permissions' },
  [TicketSubCategory.UserAccessOther]:      { id: 'subCatUserAccessOther',      defaultMessage: 'Other' },
  [TicketSubCategory.BatteryFirmware]:      { id: 'subCatBatteryFirmware',      defaultMessage: 'Battery Firmware' },
  [TicketSubCategory.InverterFirmware]:     { id: 'subCatInverterFirmware',     defaultMessage: 'Inverter Firmware' },
  [TicketSubCategory.ControllerFirmware]:   { id: 'subCatControllerFirmware',   defaultMessage: 'Controller Firmware' },
  [TicketSubCategory.ExternalEmsFirmware]:  { id: 'subCatExternalEmsFirmware',  defaultMessage: 'External EMS Firmware' },
  [TicketSubCategory.FirmwareOther]:        { id: 'subCatFirmwareOther',        defaultMessage: 'Other' },
  [TicketSubCategory.BMS]:                  { id: 'subCatBMS',                  defaultMessage: 'BMS' },
  [TicketSubCategory.ConfigMonitor]:        { id: 'subCatConfigMonitor',        defaultMessage: 'Monitor' },
  [TicketSubCategory.ExternalEMS]:          { id: 'subCatExternalEMS',          defaultMessage: 'External EMS' },
  [TicketSubCategory.ConfigurationOther]:   { id: 'subCatConfigurationOther',   defaultMessage: 'Other' },
  [TicketSubCategory.Other]:                { id: 'subCatOther',                defaultMessage: 'Other' }
};

// Active subcategories per category (for new ticket creation — no legacy entries)
export const subCategoriesByCategory: Record<number, number[]> = {
  [TicketCategory.Hardware]:       [100, 101, 102, 103, 104, 105, 199],
  [TicketCategory.Software]:       [200, 201, 202, 203, 299],
  [TicketCategory.UserAccess]:     [400, 401, 499],
  [TicketCategory.Firmware]:       [500, 501, 502, 503, 599],
  [TicketCategory.Configuration]:  [600, 601, 602, 699]
  // TicketCategory.Other (6) has no predefined subcategories — uses free-text only
};

// "Other" subcategory values per category — triggers free-text input
export const otherSubCategoryValues = new Set([199, 299, 499, 599, 699]);

// Category display labels (active categories only — used in dropdowns)
export const categoryLabels: Record<number, string> = {
  [TicketCategory.Hardware]: 'Hardware',
  [TicketCategory.Software]: 'Software',
  [TicketCategory.UserAccess]: 'User Access',
  [TicketCategory.Firmware]: 'Firmware',
  [TicketCategory.Configuration]: 'Configuration',
  [TicketCategory.Other]: 'Other'
};

export const categoryKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TicketCategory.Hardware]:      { id: 'categoryHardware',      defaultMessage: 'Hardware' },
  [TicketCategory.Software]:      { id: 'categorySoftware',      defaultMessage: 'Software' },
  [TicketCategory.UserAccess]:    { id: 'categoryUserAccess',    defaultMessage: 'User Access' },
  [TicketCategory.Firmware]:      { id: 'categoryFirmware',      defaultMessage: 'Firmware' },
  [TicketCategory.Configuration]: { id: 'categoryConfiguration', defaultMessage: 'Configuration' },
  [TicketCategory.Other]:         { id: 'categoryOther',         defaultMessage: 'Other' }
};

// Legacy category labels (for displaying old tickets with Network category)
export const legacyCategoryLabels: Record<number, string> = {
  2: 'Network (legacy)'
};

export enum TicketSource {
  Manual = 0,
  AutoAlert = 1,
  Email = 2,
  Api = 3
}

export enum CommentAuthorType {
  Human = 0,
  AiAgent = 1,
  // Admin-only note; the backend strips these from client/partner responses.
  InternalNote = 2
}

export enum DiagnosisStatus {
  Pending = 0,
  Analyzing = 1,
  Completed = 2,
  Failed = 3
}

export enum TimelineEventType {
  Created = 0,
  StatusChanged = 1,
  Assigned = 2,
  CommentAdded = 3,
  AiDiagnosisAttached = 4,
  Escalated = 5,
  ResolutionAdded = 6,
  SubjectChanged = 7
}

export type Ticket = {
  id: number;
  subject: string;
  description: string;
  status: number;
  priority: number;
  category: number;
  subCategory: number;
  source: number;
  installationId: number | null;
  assigneeId: number | null;
  createdByUserId: number;
  tags: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  rootCause: string | null;
  solution: string | null;
  preFilledFromAi: boolean;
  customSubCategory: string | null;
  customCategory: string | null;
};

export type TicketComment = {
  id: number;
  ticketId: number;
  authorType: number;
  authorId: number | null;
  body: string;
  createdAt: string;
  editedAt?: string | null;
};

export type TicketAiDiagnosis = {
  id: number;
  ticketId: number;
  status: number;
  rootCause: string | null;
  confidence: number | null;
  recommendedActions: string | null;
  similarTicketIds: string | null;
  feedback: number | null;
  overrideText: string | null;
  createdAt: string;
  completedAt: string | null;
};

export type TicketTimelineEvent = {
  id: number;
  ticketId: number;
  eventType: number;
  description: string;
  actorType: number;
  actorId: number | null;
  createdAt: string;
};

export type TicketDetail = {
  ticket: Ticket;
  comments: TicketComment[];
  diagnosis: TicketAiDiagnosis | null;
  timeline: TicketTimelineEvent[];
  installationName: string;
  installationProduct: number | null;
  creatorName: string;
  assigneeName: string | null;
};

export type TicketSummary = {
  id: number;
  subject: string;
  status: number;
  priority: number;
  category: number;
  subCategory: number;
  installationId: number | null;
  createdAt: string;
  updatedAt: string;
  installationName: string;
  distributionPartner: string;
  customSubCategory: string | null;
  customCategory: string | null;
  assigneeId: number | null;
  assigneeName: string | null;
};

export type AdminUser = {
  id: number;
  name: string;
};

// Client/partner-scoped list row (from GetMyTickets) — no assignee, priority or
// AI. Covers every ticket the caller may see: their own plus non-admin-created
// tickets on installations they have access to.
export type PartnerTicketSummary = {
  id: number;
  subject: string;
  status: number;
  category: number;
  subCategory: number;
  installationId: number | null;
  createdAt: string;
  updatedAt: string;
  installationName: string;
  customSubCategory: string | null;
  customCategory: string | null;
  createdByName: string;
  isMine: boolean;
};

// Client/partner-scoped detail (from GetMyTicketDetail) — comments only; no
// assignee, priority, AI diagnosis, timeline, root cause or solution. canEdit is
// true only for the creator; everyone else may read and comment.
export type PartnerTicketDetail = {
  createdByName: string;
  canEdit: boolean;
  id: number;
  subject: string;
  description: string;
  status: number;
  category: number;
  subCategory: number;
  customCategory: string | null;
  customSubCategory: string | null;
  createdAt: string;
  updatedAt: string;
  installationName: string;
  comments: TicketComment[];
  participants: AdminUser[];
};

export enum DiagnosisFeedback {
  Accepted = 0,
  Rejected = 1,
  Overridden = 2
}

// Helper: get display label for a subcategory, preferring custom label when available
export function getSubCategoryDisplayLabel(
  subCategory: number,
  customSubCategory: string | null | undefined
): string {
  if (otherSubCategoryValues.has(subCategory) && customSubCategory) {
    return customSubCategory;
  }
  return subCategoryLabels[subCategory] ?? 'Unknown';
}

// Helper: get display label for a category, preferring custom label when available
export function getCategoryDisplayLabel(
  category: number,
  customCategory: string | null | undefined
): string {
  if (category === TicketCategory.Other && customCategory) {
    return customCategory;
  }
  return categoryLabels[category] ?? legacyCategoryLabels[category] ?? 'Unknown';
}
